import React, { useState } from 'react';

// Components
import { ErrorLabel } from '../../../ErrorLabel/ErrorLabel';
import { Input, TextArea } from '@/views/components/inputs';
import { Label } from '../../../Label/Label';

// Libraries
import classNames from 'classnames';

// Types
import Locale from '@/types/locale';

// Utils
import { formatMessageWithLocale } from '@/utils/i18n';
import HistoricalScenario from '@/types/historical-scenario';

const LONG_DESCRIPTION_MAX_LENGTH = 425;
const NAME_MAX_LENGTH = 70;
const SHORT_DESCRIPTION_MAX_LENGTH = 125;

enum Fields {
  Description = 'description',
  DescriptionError = 'descriptionError',
  Name = 'name',
  NameError = 'nameError',
  ShortDescription = 'shortDescription',
  ShortDescriptionError = 'shortDescriptionError',
}

interface TabFieldsProps {
  i18nBasePath: string;
  loading: boolean;
  locale?: Locale;
  localeDescription?: string;
  localeName?: string;
  localeShortDescription?: string;
  onChangeField: (field: keyof HistoricalScenario, value: string) => void;
}

export const TabFields: React.FC<TabFieldsProps> = ({
  i18nBasePath,
  loading,
  locale,
  localeDescription,
  localeName,
  localeShortDescription,
  onChangeField,
}) => {
  const [formState, setFormState] = useState({
    description: localeDescription || '',
    descriptionError: false,
    name: localeName || '',
    nameError: false,
    shortDescription: localeShortDescription || '',
    shortDescriptionError: false,
  });

  const updateField = (field: string, value: string | boolean) => {
    setFormState(prev => ({ ...prev, [field]: value }));
  };

  const handleOnChangeDescription = (event: React.FormEvent<HTMLTextAreaElement>) => {
    const { value } = event.currentTarget;
    updateField(Fields.DescriptionError, !value);
    updateField(Fields.Description, value);
    onChangeField(Fields.Description, value);
  };

  const handleOnChangeName = (value: string) => {
    updateField(Fields.NameError, !value);
    updateField(Fields.Name, value);
    onChangeField(Fields.Name, value);
  };

  const handleOnChangeShortDescription = (event: React.FormEvent<HTMLTextAreaElement>) => {
    const { value } = event.currentTarget;
    updateField(Fields.ShortDescriptionError, !value);
    updateField(Fields.ShortDescription, value);
    onChangeField(Fields.ShortDescription, value);
  };

  return (
    <>
      <div className="field-container">
        <Label htmlFor="scenario-name" label={formatMessageWithLocale(`${i18nBasePath}.ScenarioName`, locale)} />
        <Input
          className={classNames('input-v2', { 'field-error': formState.nameError })}
          controlled={false}
          data-testid="tabFieldInput-input-scenarioName"
          disabled={loading}
          id="scenario-name"
          name="scenario-name"
          maxLength={NAME_MAX_LENGTH}
          onValueChange={handleOnChangeName}
          placeholder={formatMessageWithLocale(`${i18nBasePath}.ScenarioNamePlaceholder`, locale)}
          value={formState.name}
        />
        {formState.nameError && <ErrorLabel label={formatMessageWithLocale(`${i18nBasePath}.NameError`, locale)} />}
      </div>

      <div className="field-container mt-3">
        <Label htmlFor="short-description" label={formatMessageWithLocale(`${i18nBasePath}.ShortDescription`, locale)} />
        <TextArea
          className={classNames('textarea-v2', { 'field-error': formState.shortDescriptionError })}
          data-testid="tabFieldTextarea-textArea-shortDescription"
          disabled={loading}
          id="short-description"
          maxLength={SHORT_DESCRIPTION_MAX_LENGTH}
          onChange={handleOnChangeShortDescription}
          placeholder={formatMessageWithLocale(`${i18nBasePath}.ShortDescriptionPlaceholder`, locale)}
          rows={3}
          showLength
          value={formState.shortDescription}
        />
        {formState.shortDescriptionError && <ErrorLabel label={formatMessageWithLocale(`${i18nBasePath}.ShortDescriptionError`, locale)} />}
      </div>

      <div className="field-container mt-3">
        <Label htmlFor="long-description" label={formatMessageWithLocale(`${i18nBasePath}.LongDescription`, locale)} />
        <TextArea
          className={classNames('textarea-v2', { 'field-error': formState.descriptionError })}
          data-testid="tabFieldTextarea-textArea-longDescription"
          disabled={loading}
          id="long-description"
          maxLength={LONG_DESCRIPTION_MAX_LENGTH}
          onChange={handleOnChangeDescription}
          placeholder={formatMessageWithLocale(`${i18nBasePath}.LongDescriptionPlaceholder`, locale)}
          rows={6}
          showLength
          value={formState.description}
        />
        {formState.descriptionError && <ErrorLabel label={formatMessageWithLocale(`${i18nBasePath}.LongDescriptionError`, locale)} />}
      </div>
    </>
  );
};
